import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, MapPinIcon, XIcon } from 'lucide-react';
import { ArticlePage } from './ArticlePage';
import type { WebsiteContent } from '../../data/website';
interface AboutExperienceProps {
  content: WebsiteContent;
}

/**
 * The About feature, set to print standards: an Instrument Serif display face
 * for headlines, Source Serif for running text on a controlled measure, and a
 * strict rule-and-space system between departments.
 *
 * It is a real page at /about with ordinary semantic headings, so none of the
 * editorial treatment costs anything in search — the credentials are all text.
 */
export function AboutExperience({
  content
}: AboutExperienceProps) {
  const navigate = useNavigate();
  const paragraphs = content.aboutBody.split('\n').map(line => line.trim()).filter(Boolean);
  const [lead, ...rest] = paragraphs;
  const members = content.aboutMembers.filter(member => member.name.trim());
  const venues = content.aboutVenues.filter(venue => venue.venue.trim());
  const awards = content.aboutAwards.filter(award => award.title.trim());
  const endorsements = content.aboutEndorsements.filter(entry => entry.quote.trim());
  const pullQuote = endorsements[0];
  const restQuotes = pullQuote ? endorsements.slice(1) : endorsements;
  const openingImage = content.aboutHeroImageUrl.trim() || content.heroImageUrl;
  const openingAlt = content.aboutHeroImageAlt.trim() || `${content.bandName} performing live in Fort Worth, Texas`;
  const midImage = content.aboutImageUrl.trim();
  const midAlt = content.aboutImageAlt.trim() || `${content.bandName} Texas country live performance`;
  const spreadImage = content.aboutFeatureImageUrl.trim();
  const columnBreak = Math.ceil(rest.length / 2);
  const firstColumns = rest.slice(0, columnBreak);
  const lastColumns = rest.slice(columnBreak);
  const issueLine = [content.seoGenre.split(/[,·|]/)[0]?.trim(), content.seoLocation.trim()].filter(Boolean).join(' · ');
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') navigate('/');
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [navigate]);
  const runningHead = content.aboutTitle || `About ${content.bandName}`;
  const pages: Array<{
    tone?: 'paper' | 'ink';
    bleed?: boolean;
    node: React.ReactNode;
  }> = [];
  pages.push({
    node: <>
          {/* ——— Opening spread ——— */}
          <header data-id="element-743" data-mp="2sjvpfpdaz87">
            <div className="grid gap-10 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.15fr)] lg:items-end lg:gap-16" data-id="element-744" data-mp="2s1sfz4afkqs">
              {openingImage && <figure className="order-2 lg:order-1" data-id="element-745" data-mp="3782triepzrti">
                  <img src={openingImage} alt={openingAlt} className="h-[380px] w-full object-cover sm:h-[540px] lg:h-[620px]" data-id="element-746" data-mp="377kqw2xjyfcm" />
                  <figcaption className="mt-3 flex gap-2.5 text-[10.5px] leading-[1.5] text-[#171310]/50" data-id="element-747" data-mp="33nszg2lnt69m">
                    <span className="mt-[7px] h-px w-5 shrink-0 bg-[#9c4a1a]" data-id="element-748" data-mp="2yozks6umz97v" />
                    <span className="font-serif-text italic" data-id="element-749" data-mp="2une0i0frvsnn">{openingAlt}</span>
                  </figcaption>
                </figure>}

              <div className={`order-1 lg:order-2 lg:pb-8 ${openingImage ? '' : 'lg:col-span-2 lg:text-center'}`} data-id="element-750" data-mp="2krkrus56cjb5">
                <div className="mb-7 flex items-center gap-3.5" data-id="element-751" data-mp="2g02sg8isuqsa">
                  <span className="h-px w-12 bg-[#9c4a1a]" data-id="element-752" data-mp="2b17z4cn7hjtx" />
                  <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#9c4a1a]" data-id="element-753" data-mp="2b1rizywf2ial">
                    {content.aboutEyebrow || 'Feature'}
                  </span>
                </div>

                <h1 className="font-serif-display text-[56px] leading-[0.86] tracking-[-0.02em] text-[#171310] sm:text-[88px] lg:text-[104px]" data-id="element-754" data-mp="21zrxatagiq1b">
                  {content.aboutTitle || `About ${content.bandName}`}
                </h1>

                {content.aboutSeoDescription.trim() && <p className="mt-8 max-w-[38ch] font-serif-text text-[19px] italic leading-[1.55] text-[#4b3b31] sm:text-[22px]" data-id="element-755" data-mp="5wvhl27qh90w">
                    {content.aboutSeoDescription}
                  </p>}

                <div className="mt-9 flex flex-wrap items-baseline gap-x-6 gap-y-2 border-t border-[#171310]/20 pt-4" data-id="element-756" data-mp="3san1jreukwio">
                  {members.length > 0 && <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#171310]/70" data-id="element-757" data-mp="3iflgp7rluglw">
                      {members.map(member => member.name).join(' · ')}
                    </p>}
                  {content.seoLocation.trim() && <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-[#171310]/35" data-id="element-758" data-mp="12neb6m8tzc1q">
                      {content.seoLocation}
                    </p>}
                </div>
              </div>
            </div>
          </header>
      </>
  });
  pages.push({
    node: <>
          {/* ——— Opening copy with fact panel ——— */}
          <section data-id="element-759" data-mp="31ucn6dcsexpo">
            <div className="grid gap-12 lg:grid-cols-[minmax(0,2.25fr)_minmax(0,0.85fr)] lg:gap-16" data-id="element-760" data-mp="31uuohoeb424g">
              <div data-id="element-761" data-mp="2x3mjr5nx94xm">
                {lead && <p className="max-w-[46ch] font-serif-text text-[21px] leading-[1.55] text-[#171310] sm:text-[23px]" data-id="element-762" data-mp="2x21m376b95vj">
                    {lead}
                  </p>}

                <div className="article-columns mt-9 sm:columns-2 sm:gap-11" data-id="element-763" data-mp="35vy367kmbs43">
                  {firstColumns.map(paragraph => <p key={paragraph.slice(0, 40)} className="break-inside-avoid font-serif-text text-[16.5px] leading-[1.62] text-[#2e2620] [&:not(:first-child)]:indent-6" data-id="element-764" data-mp="3fune1ge3kcau">
                      {paragraph}
                    </p>)}
                </div>
              </div>

              <aside className="lg:pl-10 lg:[border-left:1px_solid_rgba(23,19,16,0.18)]" data-id="element-765" data-mp="2b6bcxp5wjqsy">
                <div className="border-t-2 border-[#171310] pt-4" data-id="element-766" data-mp="2f6lrvrbo9rjy">
                  <h2 className="text-[9.5px] font-bold uppercase tracking-[0.28em] text-[#9c4a1a]" data-id="element-767" data-mp="2k4x2435vq42p">The file</h2>
                  <dl className="mt-6 space-y-5" data-id="element-768" data-mp="2p5b4b4ksujst">
                    <FactRow label="Sound" value={content.seoGenre} data-id="element-769" data-mp="2u3cjhef3tyz0" />
                    <FactRow label="Based in" value={content.seoLocation || 'Fort Worth, Texas'} data-id="element-770" data-mp="2u345fj78wnm8" />
                    <FactRow label="Started in" value="College Station, Texas" data-id="element-771" data-mp="2y34vy27mcp9n" />
                    {awards[0] && <FactRow label={awards[0].label || 'Recognition'} value={awards[0].title} data-id="element-772" data-mp="32w7rgfvyi8sh" />}
                    {venues[0] && <FactRow label="Notable stage" value={venues[0].venue} data-id="element-773" data-mp="37tzm3hnzk90d" />}
                  </dl>
                </div>
              </aside>
            </div>
          </section>
      </>
  });
  if (spreadImage) {
    pages.push({
      bleed: true,
      node: <figure data-id="element-774" data-mp="166d58c7nn7z9">
            <img src={spreadImage} alt={content.aboutFeatureImageAlt || `${content.bandName} live performance`} loading="lazy" className="h-[340px] w-full object-cover sm:h-[620px]" data-id="element-775" data-mp="166wopxcye4fe" />
            {content.aboutFeatureImageAlt.trim() && <figcaption className="px-6 pt-3 font-serif-text text-[10.5px] italic text-[#171310]/50 sm:px-12 lg:px-16" data-id="element-776" data-mp="rafdr78oertd">
                {content.aboutFeatureImageAlt}
              </figcaption>}
          </figure>
    });
  }
  if (pullQuote) {
    pages.push({
      node: <section className="py-10 lg:py-20" data-id="element-777" data-mp="1f46wh8qkbbfk">
              <div className="mx-auto max-w-4xl text-center" data-id="element-778" data-mp="1a7ynye6veghd">
                <span className="mx-auto mb-10 block h-px w-16 bg-[#9c4a1a]" data-id="element-779" data-mp="27mknaodhdx55" />
                <blockquote className="font-serif-display text-[42px] leading-[1.04] tracking-[-0.015em] text-[#171310] sm:text-[72px] lg:text-[84px]" data-id="element-780" data-mp="27onng18cl6nl">
                  <span className="text-[#9c4a1a]" data-id="element-781" data-mp="1b81783tvzm61">“</span>
                  {pullQuote.quote}
                  <span className="text-[#9c4a1a]" data-id="element-782" data-mp="1fz9il5es1hlu">”</span>
                </blockquote>
                <div className="mt-12 flex flex-col items-center gap-5 sm:flex-row sm:justify-center sm:gap-7" data-id="element-783" data-mp="127uw2olnuya1">
                  {pullQuote.imageUrl.trim() && <img src={pullQuote.imageUrl} alt={pullQuote.artist} loading="lazy" className="h-36 w-36 rounded-full object-cover grayscale transition duration-300 ease-out hover:grayscale-0 sm:h-44 sm:w-44" data-id="element-784" data-mp="174lfnlwocc07" />}
                  <cite className="text-[13px] font-bold uppercase not-italic tracking-[0.26em] text-[#9c4a1a] sm:text-[15px]" data-id="element-785" data-mp="1pwnwax9bfsag">
                    {pullQuote.artist}
                  </cite>
                </div>
              </div>
            </section>
    });
  }
  if (lastColumns.length > 0 || midImage) {
    pages.push({
      node: <section data-id="element-786" data-mp="6wogpkwdz1rs">
              <div className="grid gap-12 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.5fr)] lg:gap-16" data-id="element-787" data-mp="1zesu9onay62">
                {midImage && <figure className="lg:sticky lg:top-24 lg:self-start" data-id="element-788" data-mp="fzfpte1shlg9">
                    <img src={midImage} alt={midAlt} loading="lazy" className="h-auto w-full object-cover" data-id="element-789" data-mp="b0cni1d71tbz" />
                    <figcaption className="mt-3 flex gap-2.5 text-[10.5px] leading-[1.5] text-[#171310]/50" data-id="element-790" data-mp="3jfek42naivil">
                      <span className="mt-[7px] h-px w-5 shrink-0 bg-[#9c4a1a]" data-id="element-791" data-mp="3ff42fsq4ygnw" />
                      <span className="font-serif-text italic" data-id="element-792" data-mp="3t5fy21lxixjs">{midAlt}</span>
                    </figcaption>
                  </figure>}
                <div className="max-w-[68ch]" data-id="element-793" data-mp="3ododq8td9o23">
                  {lastColumns.map((paragraph, index) => <p key={paragraph.slice(0, 40)} className={`font-serif-text text-[16.5px] leading-[1.62] text-[#2e2620] ${index === 0 ? '' : 'indent-6'}`} data-id="element-794" data-mp="30nbyecvskp1h" data-mpi={index}>
                      {paragraph}
                    </p>)}

                  {content.aboutSharedStage.trim() && <div className="mt-12 border-t-2 border-[#171310] pt-5" data-id="element-795" data-mp="1n4zwpo4d4e8r">
                      <DepartmentLabel index="Company" data-id="element-796" data-mp="1j1abn8mwcesh" />
                      <h2 className="mt-3 font-serif-display text-[30px] leading-[0.95] tracking-[-0.01em] text-[#171310] sm:text-[38px]" data-id="element-797" data-mp="1e2qnd1ktyqwy">
                        Shared the Stage
                      </h2>
                      <p className="mt-4 font-serif-text text-[16.5px] leading-[1.62] text-[#2e2620]" data-id="element-798" data-mp="14bvx7m4gr768">
                        {content.aboutSharedStage}
                      </p>
                    </div>}
                </div>
              </div>
            </section>
    });
  }
  if (venues.length > 0) {
    pages.push({
      tone: 'ink',
      node: <section aria-labelledby="live-performances" data-id="element-799" data-mp="320re8coy2cgj">
              <div className="mx-auto max-w-5xl" data-id="element-800" data-mp="322caq7utk5h1">
                <div className="mb-6 flex items-center gap-3.5" data-id="element-801" data-mp="36yjgo0n7tgyz">
                  <span className="h-px w-12 bg-[#d98b4a]" data-id="element-802" data-mp="3b0wm0ny1vcv8" />
                  <span className="text-[9.5px] font-bold uppercase tracking-[0.3em] text-[#d98b4a]" data-id="element-803" data-mp="3b1frv6afeasw">
                    On stage
                  </span>
                </div>
                <h2 id="live-performances" className="font-serif-display text-[42px] leading-[0.9] tracking-[-0.015em] sm:text-[62px]" data-id="element-804" data-mp="2jarum5bvhpki">
                  Live Performances
                </h2>
                {content.aboutVenuesIntro.trim() && <p className="mt-5 max-w-[54ch] font-serif-text text-[16.5px] leading-[1.6] text-[#f5f1e8]/60" data-id="element-805" data-mp="2x26nr4zhv75a">
                    {content.aboutVenuesIntro}
                  </p>}

                <ol className="mt-12 border-t border-[#f5f1e8]/15" data-id="element-806" data-mp="24jsqb6i2ln6q">
                  {venues.map((venue, index) => <li key={venue.id} className="flex flex-col gap-1.5 border-b border-[#f5f1e8]/15 py-6 sm:flex-row sm:items-baseline sm:justify-between sm:gap-8" data-id="element-807" data-mp="37u6qo8wni0xy" data-mpi={index}>
                      <div className="flex min-w-0 items-baseline gap-5" data-id="element-808" data-mp="3cmgf1ki4asy4">
                        <span className="w-7 shrink-0 font-serif-text text-[13px] tabular-nums text-[#d98b4a]/70" data-id="element-809" data-mp="3cn9ncpwq5qid">
                          {String(index + 1).padStart(2, '0')}
                        </span>
                        <div className="min-w-0" data-id="element-810" data-mp="2jzvm4flzu8sg">
                          <h3 className="font-serif-display text-[28px] leading-[1.05] tracking-[-0.01em] sm:text-[34px]" data-id="element-811" data-mp="2xyn0820uvugl">
                            {venue.venue}
                          </h3>
                          {venue.detail.trim() && <span className="mt-1.5 inline-block text-[9.5px] font-bold uppercase tracking-[0.22em] text-[#d98b4a]" data-id="element-812" data-mp="d960lnjqzq29">
                              {venue.detail}
                            </span>}
                        </div>
                      </div>
                      {venue.city.trim() && <p className="flex shrink-0 items-center gap-1.5 pl-12 font-serif-text text-[13.5px] italic text-[#f5f1e8]/45 sm:pl-0" data-id="element-813" data-mp="6rw6mmyqh6vh">
                          <MapPinIcon className="h-3.5 w-3.5 not-italic" data-id="element-814" data-mp="bsa2l6n28qd5" /> {venue.city}
                        </p>}
                    </li>)}
                </ol>
              </div>
            </section>
    });
  }
  if (awards.length > 0) {
    pages.push({
      node: <section aria-labelledby="awards" data-id="element-815" data-mp="up1rpwpa9zp4">
              <div className="mx-auto max-w-3xl text-center" data-id="element-816" data-mp="unqopz3nwvz2">
                <DepartmentLabel index="Recognition" centered data-id="element-817" data-mp="1iew287m4rc67" />
                <h2 id="awards" className="mt-3 font-serif-display text-[36px] leading-[0.95] tracking-[-0.015em] text-[#171310] sm:text-[48px]" data-id="element-818" data-mp="1icjd9ahyg1jn">
                  Awards &amp; Recognition
                </h2>

                <ul className="mt-12 space-y-8" data-id="element-819" data-mp="3qm9fffg5u3pm">
                  {awards.map(award => <li key={award.id} className="border-y border-[#171310]/25 py-10" data-id="element-820" data-mp="3lpai6o179gn7">
                      {award.label.trim() && <p className="text-[10px] font-bold uppercase tracking-[0.34em] text-[#9c4a1a]" data-id="element-821" data-mp="3gr928bz2lxfy">
                          {award.label}
                        </p>}
                      <div className="mt-4 flex flex-col items-center justify-center gap-5 sm:flex-row sm:gap-7" data-id="element-822" data-mp="d97k99zw36c2">
                        <h3 className="font-serif-display text-[30px] leading-[1.02] tracking-[-0.01em] text-[#171310] sm:text-[42px]" data-id="element-823" data-mp="8fcwnvh5y77p">
                          {award.title}
                        </h3>
                        {award.logoUrl.trim() && (award.logoLinkUrl.trim() ? <a href={award.logoLinkUrl} target="_blank" rel="noreferrer" className="shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#9c4a1a]" data-id="element-824" data-mp="13iojijn33yzd">
                              <img src={award.logoUrl} alt={`${award.title} presenter logo`} loading="lazy" className="h-10 w-auto object-contain sm:h-12" data-id="element-825" data-mp="1igqrr75298my" />
                            </a> : <img src={award.logoUrl} alt={`${award.title} presenter logo`} loading="lazy" className="h-10 w-auto shrink-0 object-contain sm:h-12" data-id="element-826" data-mp="216qcxixs63l3" />)}
                      </div>
                      {award.detail.trim() && <p className="mx-auto mt-4 max-w-[48ch] font-serif-text text-[15px] italic leading-[1.55] text-[#171310]/55" data-id="element-827" data-mp="643app7wmp3k">
                          {award.detail}
                        </p>}
                    </li>)}
                </ul>
              </div>
            </section>
    });
  }
  if (restQuotes.length > 0) {
    pages.push({
      node: <section aria-labelledby="endorsements" data-id="element-828" data-mp="osqiifhgtsku">
              <div className="mx-auto max-w-5xl" data-id="element-829" data-mp="12t1699nhar1s">
                <DepartmentLabel index="In their words" data-id="element-830" data-mp="12srilspwovz9" />
                <h2 id="endorsements" className="mt-3 font-serif-display text-[38px] leading-[0.92] tracking-[-0.015em] text-[#171310] sm:text-[54px]" data-id="element-831" data-mp="yr6a0jken3uv">
                  What Other Artists Are Saying
                </h2>

                <div className="mt-14 space-y-14" data-id="element-832" data-mp="2qf48yge6ab39">
                  {restQuotes.map((entry, index) => {
              const flipped = index % 2 === 1;
              return <figure key={entry.id} className={`grid items-center gap-7 border-t border-[#171310]/20 pt-9 sm:gap-11 md:grid-cols-[minmax(0,190px)_minmax(0,1fr)] ${flipped ? 'md:grid-cols-[minmax(0,1fr)_minmax(0,190px)]' : ''}`} data-id="element-833" data-mp="2uik1p1n4frao" data-mpi={index}>
                        {entry.imageUrl.trim() && <img src={entry.imageUrl} alt={entry.artist} loading="lazy" className={`h-[190px] w-[190px] object-cover grayscale transition duration-300 ease-out hover:grayscale-0 ${flipped ? 'md:order-2' : ''}`} data-id="element-834" data-mp="1xqq8zv3ix4p2" />}
                        <div className={flipped ? 'md:order-1' : ''} data-id="element-835" data-mp="19i42hum2m681">
                          <blockquote className="max-w-[34ch] font-serif-display text-[26px] leading-[1.16] tracking-[-0.01em] text-[#171310] sm:text-[34px]" data-id="element-836" data-mp="14j9ac227r5b9">
                            “{entry.quote}”
                          </blockquote>
                          <figcaption className="mt-5 flex items-center gap-3" data-id="element-837" data-mp="vqyzcijhuvsy">
                            <span className="h-px w-8 bg-[#9c4a1a]" data-id="element-838" data-mp="qsxim49jj8kn" />
                            <span className="text-[10.5px] font-bold uppercase tracking-[0.24em] text-[#9c4a1a]" data-id="element-839" data-mp="lsrv90ab088d">
                              {entry.artist}
                            </span>
                          </figcaption>
                        </div>
                      </figure>;
            })}
                </div>
              </div>
            </section>
    });
  }
  pages.push({
    tone: 'ink',
    node: <section className="text-center" aria-labelledby="about-cta" data-id="element-840" data-mp="2749urb1a10qy">
            <div className="mx-auto max-w-2xl" data-id="element-841" data-mp="3p3hid5e477no">
              <span className="mx-auto mb-8 block h-px w-14 bg-[#d98b4a]" data-id="element-842" data-mp="3u2u41mt928oo" />
              <h2 id="about-cta" className="font-serif-display text-[44px] leading-[0.92] tracking-[-0.015em] sm:text-[64px]" data-id="element-843" data-mp="3u0zl2e1jgqn8">
                {content.aboutCtaHeadline || `Hear ${content.bandName}`}
              </h2>
              {content.aboutCtaBody.trim() && <p className="mx-auto mt-5 max-w-[44ch] font-serif-text text-[17px] leading-[1.6] text-[#f5f1e8]/65" data-id="element-844" data-mp="3b485ael7gtnx">
                  {content.aboutCtaBody}
                </p>}
              <div className="mt-10 flex flex-wrap items-center justify-center gap-3" data-id="element-845" data-mp="3k4whd3r1gjy6">
                <Link to="/#music" className="bg-[#d98b4a] px-6 py-3.5 text-[11px] font-bold uppercase tracking-[0.16em] text-[#1d1008] transition-colors duration-150 ease-out hover:bg-[#eda168] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#d98b4a] focus-visible:ring-offset-2 focus-visible:ring-offset-[#171310]" data-id="element-846" data-mp="3k2jq1zze4x8g">
                  Listen to the music
                </Link>
                <Link to="/#shows" className="border border-[#f5f1e8]/25 px-6 py-3.5 text-[11px] font-bold uppercase tracking-[0.16em] transition-colors duration-150 ease-out hover:border-[#d98b4a] hover:text-[#eda168] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#d98b4a]" data-id="element-847" data-mp="1am96svt3inkz">
                  See upcoming shows
                </Link>
                <Link to="/#contact" className="border border-[#f5f1e8]/25 px-6 py-3.5 text-[11px] font-bold uppercase tracking-[0.16em] transition-colors duration-150 ease-out hover:border-[#d98b4a] hover:text-[#eda168] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#d98b4a]" data-id="element-848" data-mp="rtx8ck5kc6g4">
                  Book {content.bandName}
                </Link>
              </div>
            </div>
          </section>
  });
  return <main className="min-h-screen w-full bg-[#0b0908] pt-[52px]" data-id="element-849" data-mp="3nsz5rd5jqzz2">
      {/* Running head — fixed, so closing the article is always one click away */}
      <div className="fixed inset-x-0 top-0 z-50 flex items-center justify-between gap-4 border-b border-[#f5f1e8]/10 bg-[#0b0908]/94 px-5 py-3.5 backdrop-blur sm:px-8" data-id="element-850" data-mp="3e24flxp6jg8c">
        <Link to="/" className="group inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#f5f1e8] transition-colors duration-150 ease-out hover:text-[#d98b4a] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#d98b4a]" data-id="element-851" data-mp="394co34sg5w4o">
          <ArrowLeftIcon className="h-3.5 w-3.5 transition-transform duration-200 ease-out group-hover:-translate-x-0.5" data-id="element-852" data-mp="2v5d07zcrdl9k" />
          {content.bandName}
        </Link>
        {issueLine && <span className="hidden text-[10px] font-medium uppercase tracking-[0.24em] text-[#f5f1e8]/35 md:inline" data-id="element-853" data-mp="tcosbcp669ng">
            {issueLine}
          </span>}
        <div className="flex items-center gap-3" data-id="element-854" data-mp="2sjvtnv6fqt3h">
          <span className="hidden text-[10px] font-medium uppercase tracking-[0.2em] text-[#f5f1e8]/35 sm:inline" data-id="element-855" data-mp="2skneqc2ep8fi">
            Esc to close
          </span>
          <Link to="/" aria-label="Close article" className="rounded-full border border-[#f5f1e8]/25 p-1.5 text-[#f5f1e8] transition-colors duration-150 ease-out hover:border-[#d98b4a] hover:text-[#d98b4a] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#d98b4a]" data-id="element-856" data-mp="2itsqx39ii0ry">
            <XIcon className="h-3.5 w-3.5" data-id="element-857" data-mp="2wmic55h7v1o0" />
          </Link>
        </div>
      </div>

      <article className="flex flex-col gap-6 py-6 lg:gap-10 lg:py-10" data-id="element-858" data-mp="31hny24amcgc3">
        {pages.map((page, index) => <ArticlePage key={index} folio={index + 1} runningHead={runningHead} tone={page.tone} bleed={page.bleed} data-id="element-859" data-mp="1l7dbe70nbd71" data-mpi={index}>
            {page.node}
          </ArticlePage>)}
      </article>

      <footer className="mx-auto flex max-w-[1180px] flex-col items-center gap-4 px-6 pb-12 pt-2 text-center sm:flex-row sm:justify-between sm:px-12" data-id="element-860" data-mp="36kpqd2ab57p">
        <Link to="/" className="group inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#f5f1e8] transition-colors duration-150 ease-out hover:text-[#d98b4a] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#d98b4a]" data-id="element-861" data-mp="d5tpxnnqtg2i">
          <ArrowLeftIcon className="h-3.5 w-3.5 transition-transform duration-200 ease-out group-hover:-translate-x-0.5" data-id="element-862" data-mp="3udx8uxmq9sq9" />
          Back to {content.bandName}
        </Link>
        <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-[#f5f1e8]/30" data-id="element-863" data-mp="3pl40lzs1w29f">
          © {new Date().getFullYear()} {content.bandName} · {content.seoLocation || 'Texas'}
        </p>
      </footer>
    </main>;
}

/** A labelled fact in the sidebar panel. */
function FactRow({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  if (!value.trim()) return null;
  return <div data-id="element-864" data-mp="3fkmiv16ci4sz">
      <dt className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#171310]/40" data-id="element-865" data-mp="3fia6r4n9qonu">{label}</dt>
      <dd className="mt-2 font-serif-display text-[28px] leading-[1.08] tracking-[-0.01em] text-[#171310] sm:text-[32px]" data-id="element-866" data-mp="3khmqhgiuffm7">
        {value}
      </dd>
    </div>;
}

/** Numbered department marker, the way a print feature signposts sections. */
function DepartmentLabel({
  index,
  centered = false
}: {
  index: string;
  centered?: boolean;
}) {
  return <div className={`flex items-center gap-3.5 ${centered ? 'justify-center' : ''}`} data-id="element-867" data-mp="75kzi1l9ez4b">
      <span className="h-px w-12 bg-[#9c4a1a]" data-id="element-868" data-mp="c3wa4ejdpdnl" />
      <span className="text-[9.5px] font-bold uppercase tracking-[0.3em] text-[#9c4a1a]" data-id="element-869" data-mp="c1ju46x6ldd6">{index}</span>
    </div>;
}